import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { AlertCircle, ShieldCheck } from "lucide-react";

const SUBSTANCES = ["Álcool", "Cocaína", "Crack", "Maconha", "Medicamentos", "Outras"];

// Rótulos amigáveis do intent — usados na tarja do topo da tela + payload.
const INTENT_LABELS = {
  triagem:    { label: "Triagem urgente (emergência)", color: "#DC2626", bg: "#FEE2E2" },
  orientacao: { label: "Só orientação (sem internação)", color: "#0B2B62", bg: "#EFF6FF" },
  internacao: { label: "Buscar clínica para internação", color: "#059669", bg: "#ECFDF5" },
  orcamento:  { label: "Comparar orçamentos de clínicas", color: "#B45309", bg: "#FEF3C7" },
  consulta:   { label: "Agendar consulta com profissional", color: "#7C3AED", bg: "#F5F3FF" },
};

const PROFESSIONAL_TYPES = [
  "Qualquer profissional (a IA sugere)",
  "Psiquiatra",
  "Psicólogo(a)",
  "Terapeuta / Psicanalista",
  "Nutricionista",
  "Terapeuta ocupacional",
  "Enfermagem psiquiátrica",
  "Outro",
];

const RequestHelp = () => {
  const nav = useNavigate();
  const [qs] = useSearchParams();
  const intent = useMemo(() => {
    const v = qs.get("intent");
    return INTENT_LABELS[v] ? v : null;
  }, [qs]);
  const isConsulta = intent === "consulta";
  const [form, setForm] = useState({
    patient_name: "",
    patient_age: "",
    city: "",
    substance: "Álcool",
    situation: "",
    reporter_relation: "familiar",
    // Campos específicos de consulta (só enviados quando intent=consulta)
    professional_type: PROFESSIONAL_TYPES[0],
    previous_treatment: "",
    medications: "",
    consultation_reason: "",
    // LGPD — checkboxes obrigatórios (Art. 7 + Art. 11 + Termos)
    consent_lgpd: false,
    consent_sensitive_data: false,
    consent_terms: false,
  });
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!(form.consent_lgpd && form.consent_sensitive_data && form.consent_terms)) {
      toast.error("Para prosseguir, marque os 3 consentimentos LGPD abaixo.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        patient_name: form.patient_name,
        patient_age: parseInt(form.patient_age) || 0,
        city: form.city,
        substance: form.substance,
        situation: form.situation,
        reporter_relation: form.reporter_relation,
        family_intent: intent,
        consent_lgpd: form.consent_lgpd,
        consent_sensitive_data: form.consent_sensitive_data,
        consent_terms: form.consent_terms,
      };
      if (isConsulta) {
        payload.professional_type = form.professional_type;
        payload.previous_treatment = form.previous_treatment;
        payload.medications = form.medications;
        payload.consultation_reason = form.consultation_reason;
      }
      const { data } = await api.post("/cases", payload);
      toast.success("Caso criado. Iniciando triagem...");
      nav(`/triagem/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao criar caso");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 text-red-600 text-xs uppercase tracking-widest font-semibold mb-3">
          <AlertCircle className="w-4 h-4" /> Pedido de ajuda
        </div>
        <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0B2B62] tracking-tight">Conte-nos o que está acontecendo</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Preencha o essencial. Em menos de um minuto nossa IA inicia a triagem estruturada.</p>
        {intent && (
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border font-semibold text-xs"
               data-testid="request-intent-badge"
               style={{ color: INTENT_LABELS[intent].color, borderColor: INTENT_LABELS[intent].color, background: INTENT_LABELS[intent].bg }}>
            <AlertCircle className="w-3.5 h-3.5" /> Objetivo declarado: {INTENT_LABELS[intent].label}
          </div>
        )}
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-6 md:p-8 space-y-5" data-testid="request-help-form">
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="text-sm font-medium text-slate-700">Nome do paciente</label>
            <input data-testid="input-patient-name" required value={form.patient_name} onChange={update("patient_name")}
              className="mt-1.5 w-full px-3 py-2.5 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#0B2B62] outline-none" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Idade</label>
            <input data-testid="input-patient-age" required type="number" min="1" max="120" value={form.patient_age} onChange={update("patient_age")}
              className="mt-1.5 w-full px-3 py-2.5 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#0B2B62] outline-none" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="text-sm font-medium text-slate-700">Cidade</label>
            <input data-testid="input-city" required value={form.city} onChange={update("city")}
              className="mt-1.5 w-full px-3 py-2.5 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#0B2B62] outline-none" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Substância principal</label>
            <select data-testid="select-substance" value={form.substance} onChange={update("substance")}
              className="mt-1.5 w-full px-3 py-2.5 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#0B2B62] outline-none bg-white">
              {SUBSTANCES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-slate-700">Quem está solicitando</label>
          <select data-testid="select-relation" value={form.reporter_relation} onChange={update("reporter_relation")}
            className="mt-1.5 w-full px-3 py-2.5 border border-slate-300 rounded-md bg-white outline-none focus:ring-2 focus:ring-[#0B2B62]">
            <option value="familiar">Familiar próximo</option>
            <option value="conjuge">Cônjuge / companheiro(a)</option>
            <option value="amigo">Amigo(a)</option>
            <option value="proprio">O próprio paciente</option>
            <option value="outro">Outro</option>
          </select>
        </div>

        <div>
          <label className="text-sm font-medium text-slate-700">{isConsulta ? "Contexto adicional (sintomas, gatilhos)" : "O que está acontecendo neste momento?"}</label>
          <textarea data-testid="input-situation" required rows={5} value={form.situation} onChange={update("situation")}
            placeholder={isConsulta
              ? "Ex: ansiedade constante, dificuldade pra dormir há 2 semanas, quero orientação profissional..."
              : "Descreva com suas palavras. Ex: está em surto, sem dormir há 3 dias, agressivo com a família..."}
            className="mt-1.5 w-full px-3 py-2.5 border border-slate-300 rounded-md focus:ring-2 focus:ring-[#0B2B62] outline-none resize-none" />
        </div>

        {isConsulta && (
          <div className="grid gap-5 border-t border-violet-200 pt-5 mt-2" data-testid="consulta-extra-fields">
            <div className="text-[10px] uppercase tracking-widest text-violet-700 font-bold">Triagem para consulta com profissional</div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-medium text-slate-700">Tipo de profissional desejado</label>
                <select data-testid="select-professional-type" value={form.professional_type} onChange={update("professional_type")}
                  className="mt-1.5 w-full px-3 py-2.5 border border-slate-300 rounded-md bg-white outline-none focus:ring-2 focus:ring-violet-500">
                  {PROFESSIONAL_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Objetivo principal da consulta</label>
                <input data-testid="input-consultation-reason" value={form.consultation_reason} onChange={update("consultation_reason")}
                  placeholder="Ex: entender diagnóstico, ajustar medicação..."
                  className="mt-1.5 w-full px-3 py-2.5 border border-slate-300 rounded-md focus:ring-2 focus:ring-violet-500 outline-none" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Tratamentos/consultas anteriores</label>
              <textarea data-testid="input-previous-treatment" rows={2} value={form.previous_treatment} onChange={update("previous_treatment")}
                placeholder="Ex: já fiz terapia por 6 meses em 2024, uma internação em 2023..."
                className="mt-1.5 w-full px-3 py-2.5 border border-slate-300 rounded-md focus:ring-2 focus:ring-violet-500 outline-none resize-none" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Medicações em uso</label>
              <textarea data-testid="input-medications" rows={2} value={form.medications} onChange={update("medications")}
                placeholder="Liste nomes + dosagens. Se nenhuma, escreva 'nenhuma'."
                className="mt-1.5 w-full px-3 py-2.5 border border-slate-300 rounded-md focus:ring-2 focus:ring-violet-500 outline-none resize-none" />
            </div>
          </div>
        )}

        {/* Consentimentos LGPD — obrigatórios para submissão */}
        <div className="mt-2 rounded-xl border-2 border-emerald-200 bg-emerald-50/40 p-4 sm:p-5 space-y-3" data-testid="lgpd-consent-block">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-800 font-bold">
            <ShieldCheck className="w-3.5 h-3.5" />
            Consentimentos exigidos por lei (LGPD)
          </div>

          <label className="flex items-start gap-2 cursor-pointer" data-testid="consent-lgpd-label">
            <input type="checkbox" required checked={form.consent_lgpd}
              onChange={(e) => setForm({ ...form, consent_lgpd: e.target.checked })}
              data-testid="consent-lgpd" className="mt-1 h-4 w-4 accent-emerald-700 shrink-0" />
            <span className="text-xs sm:text-sm text-slate-800 leading-relaxed">
              Li e concordo com a{" "}
              <Link to="/politica-de-privacidade" target="_blank" className="text-[#0B2B62] underline">
                Política de Privacidade
              </Link>{" "}
              e autorizo o tratamento dos meus dados pessoais para as finalidades descritas (Art. 7º LGPD). *
            </span>
          </label>

          <label className="flex items-start gap-2 cursor-pointer" data-testid="consent-sensitive-label">
            <input type="checkbox" required checked={form.consent_sensitive_data}
              onChange={(e) => setForm({ ...form, consent_sensitive_data: e.target.checked })}
              data-testid="consent-sensitive" className="mt-1 h-4 w-4 accent-emerald-700 shrink-0" />
            <span className="text-xs sm:text-sm text-slate-800 leading-relaxed">
              Consinto de forma específica e destacada com o tratamento dos meus{" "}
              <strong>dados sensíveis de saúde</strong> (histórico, substâncias, medicações, contexto
              clínico) para triagem, orientação e encaminhamento — nos termos do{" "}
              <Link to="/termo-de-confidencialidade" target="_blank" className="text-[#0B2B62] underline">
                Termo de Confidencialidade
              </Link>{" "}
              e do Art. 11 da LGPD. *
            </span>
          </label>

          <label className="flex items-start gap-2 cursor-pointer" data-testid="consent-terms-label">
            <input type="checkbox" required checked={form.consent_terms}
              onChange={(e) => setForm({ ...form, consent_terms: e.target.checked })}
              data-testid="consent-terms" className="mt-1 h-4 w-4 accent-emerald-700 shrink-0" />
            <span className="text-xs sm:text-sm text-slate-800 leading-relaxed">
              Li e aceito os{" "}
              <Link to="/termos-de-uso" target="_blank" className="text-[#0B2B62] underline">
                Termos de Uso
              </Link>{" "}
              da plataforma RAF Direct. *
            </span>
          </label>

          <p className="text-[11px] text-slate-500 leading-relaxed pt-2 border-t border-emerald-100">
            Seus dados são criptografados em trânsito (HTTPS/TLS) e armazenados com controle de acesso
            por perfil. Você pode revogar seu consentimento e exercer seus direitos LGPD a qualquer momento em{" "}
            <Link to="/meus-dados" target="_blank" className="text-[#0B2B62] underline">/meus-dados</Link>.
          </p>
        </div>

        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-slate-500">Dados criptografados · LGPD Art. 7 + Art. 11</p>
          <button data-testid="submit-request-help"
            disabled={busy || !(form.consent_lgpd && form.consent_sensitive_data && form.consent_terms)}
            type="submit"
            className={`px-6 py-3 text-white rounded-md font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${isConsulta ? "bg-violet-600 hover:bg-violet-700" : "bg-[#DC2626] hover:bg-red-700"}`}>
            {busy ? "Criando caso..." : isConsulta ? "Enviar triagem para o consultor" : "Iniciar triagem inteligente"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RequestHelp;
